import React from 'react';
import { Menu, X, Globe, User, ChevronDown, Search, Mountain, HandMetal, Trophy, Sparkles, Phone, Mail, Facebook, Instagram, Youtube } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '../auth/AuthContext';
import { hasAnyAdminScope, STAFF_CTA_LABEL, ROLE_HOME } from '../auth/ProtectedRoute';
import { RESOURCE_SECTIONS, tSectionLabel } from '../data/resourceSections';

const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [resOpen, setResOpen] = React.useState(false);   // desktop dropdown open?
  const [mobResOpen, setMobResOpen] = React.useState(false); // mobile sub-section open?
  // Per-href open state for ALL simple-dropdowns (Hymettus, Athletes, …) so
  // multiple dropdowns can coexist without stealing each other's state.
  const [simpleOpen, setSimpleOpen] = React.useState({});
  const [mobSimpleOpen, setMobSimpleOpen] = React.useState({});
  const resRef = React.useRef(null);
  const simpleRefs = React.useRef({});

  // Close desktop dropdowns on outside click
  React.useEffect(() => {
    const onDocClick = (e) => {
      if (resRef.current && !resRef.current.contains(e.target)) setResOpen(false);
      // For each open simple-dropdown, close if click was outside its anchor.
      setSimpleOpen((prev) => {
        const next = { ...prev };
        Object.keys(prev).forEach((k) => {
          const el = simpleRefs.current[k];
          if (prev[k] && el && !el.contains(e.target)) next[k] = false;
        });
        return next;
      });
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Close dropdown / mobile menu on route change
  React.useEffect(() => {
    setResOpen(false);
    setIsMenuOpen(false);
    setMobResOpen(false);
    setSimpleOpen({});
    setMobSimpleOpen({});
  }, [location.pathname]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'gr' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
    // Close any open dropdown / mobile menu so the user lands on a clean state
    setResOpen(false);
    setSimpleOpen({});
    setMobResOpen(false);
    setMobSimpleOpen({});
    setIsMenuOpen(false);
  };

  const isGr = i18n.language === 'gr';

  // Hymettus sub-pages (fixed, only 2 items)
  const hymettusChildren = [
    {
      href: '/hymettus',
      icon: Mountain,
      en: 'Hymettus Trails',
      gr: 'Μονοπάτια Υμηττού',
      testId: 'hymettus-trails',
    },
    {
      href: '/hymettus/climbing',
      icon: HandMetal,
      en: 'Hymettus Climbing Crags',
      gr: 'Αναρριχητικά Πεδία Υμηττού',
      testId: 'hymettus-climbing',
    },
  ];

  const athletesChildren = [
    {
      href: '/athletes/our-team',
      icon: Trophy,
      en: 'Athletes & Competitions',
      gr: 'Αθλητές & αγώνες',
      testId: 'athletes-our-team',
    },
    {
      href: '/athletes/become',
      icon: Sparkles,
      en: 'Become an athlete',
      gr: 'Γίνε αθλητής',
      testId: 'athletes-become',
    },
  ];

  const navLinks = [
    { name: t('nav.expeditions'),     href: '/expeditions',  type: 'link' },
    { name: t('nav.classes'),         href: '/classes',      type: 'link' },
    { name: t('nav.athletes'),        href: '/athletes/our-team',  type: 'simple-dropdown', children: athletesChildren },
    { name: t('nav.hymettus'),        href: '/hymettus',     type: 'simple-dropdown', children: hymettusChildren },
    { name: t('nav.resources'),       href: '/usefull',      type: 'dropdown' },
    { name: t('nav.photosVideos'),    href: 'https://www.facebook.com/AlpineClubIlioupolis/photos_albums/?id=100058254196636&sk=photos_albums', type: 'external' },
  ];

  const isActive = (href) => {
    if (!href || href.startsWith('#')) return false;
    if (href === '/usefull') return location.pathname.startsWith('/usefull');
    if (href === '/hymettus') return location.pathname.startsWith('/hymettus');
    return location.pathname === href;
  };

  const renderDesktopLink = (link) => {
    if (link.type === 'external') {
      return (
        <a
          key={link.name}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-700 hover:text-gray-900 font-medium transition-colors"
        >
          {link.name}
        </a>
      );
    }
    if (link.type === 'simple-dropdown') {
      const active = isActive(link.href);
      const key = link.href;
      const open = !!simpleOpen[key];
      const menuId = `header-${key.replace(/^\//, '').replace(/\//g, '-')}-menu`;
      return (
        <div key={link.name} className="relative" ref={(el) => { simpleRefs.current[key] = el; }}>
          <button
            type="button"
            onClick={() => setSimpleOpen((s) => ({ ...s, [key]: !s[key] }))}
            className={`flex items-center gap-1 font-medium transition-colors ${
              active ? 'text-gray-900 border-b-2 border-[#1e3a8a]' : 'text-gray-700 hover:text-gray-900'
            }`}
            data-testid={`header-${key.replace(/^\//, '').replace(/\//g, '-')}-trigger`}
          >
            {link.name}
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {open && (
            <div
              className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50"
              role="menu"
              data-testid={menuId}
            >
              {link.children.map((c) => {
                const Icon = c.icon;
                return (
                  <Link
                    key={c.href}
                    to={c.href}
                    className="flex items-start gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#1e3a8a]"
                    role="menuitem"
                    data-testid={`header-${c.testId}`}
                  >
                    <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                    <span className="font-medium">{isGr ? c.gr : c.en}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    if (link.type === 'dropdown') {
      return (
        <div key={link.name} className="relative" ref={resRef}>
          <button
            type="button"
            onClick={() => setResOpen((v) => !v)}
            className={`inline-flex items-center gap-1 font-medium transition-colors ${
              isActive(link.href) ? 'text-gray-900 border-b-2 border-[#1e3a8a]' : 'text-gray-700 hover:text-gray-900'
            }`}
            data-testid="header-resources-trigger"
            aria-expanded={resOpen}
          >
            {link.name}
            <ChevronDown className={`w-4 h-4 transition-transform ${resOpen ? 'rotate-180' : ''}`} />
          </button>
          {resOpen && (
            <div
              className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50"
              data-testid="header-resources-menu"
              role="menu"
            >
              <Link
                to="/usefull"
                className="block px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-[#1e3a8a]"
                data-testid="header-resources-hub"
                role="menuitem"
              >
                {i18n.language === 'gr' ? 'Όλα · Επισκόπηση' : 'All · Overview'}
              </Link>
              <div className="border-t border-gray-100 my-1" />
              {RESOURCE_SECTIONS.map((s) => {
                const Icon = s.icon;
                const to = s.kind === 'static' && s.static_path ? s.static_path : `/usefull/${s.slug}`;
                return (
                  <Link
                    key={s.slug}
                    to={to}
                    className="flex items-start gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#1e3a8a]"
                    data-testid={`header-resources-${s.slug}`}
                    role="menuitem"
                  >
                    <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                    <span className="font-medium">{tSectionLabel(s, i18n.language)}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    if (link.href.startsWith('#')) {
      return (
        <a key={link.name} href={link.href} className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
          {link.name}
        </a>
      );
    }
    return (
      <Link
        key={link.name}
        to={link.href}
        className={`font-medium transition-colors ${
          isActive(link.href) ? 'text-gray-900 border-b-2 border-[#1e3a8a]' : 'text-gray-700 hover:text-gray-900'
        }`}
      >
        {link.name}
      </Link>
    );
  };

  const renderMobileLink = (link) => {
    if (link.type === 'external') {
      return (
        <a
          key={link.name}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-gray-700 hover:text-gray-900 font-medium py-2"
          onClick={() => setIsMenuOpen(false)}
        >
          {link.name}
        </a>
      );
    }
    if (link.type === 'simple-dropdown') {
      const key = link.href;
      const open = !!mobSimpleOpen[key];
      const idStub = key.replace(/^\//, '').replace(/\//g, '-');
      return (
        <div key={link.name}>
          <button
            type="button"
            onClick={() => setMobSimpleOpen((s) => ({ ...s, [key]: !s[key] }))}
            className="w-full flex items-center justify-between py-2 font-medium text-gray-700 hover:text-gray-900"
            data-testid={`header-mobile-${idStub}-trigger`}
          >
            <span>{link.name}</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {open && (
            <div className="pl-3 border-l-2 border-gray-100 ml-1 space-y-1">
              {link.children.map((c) => (
                <Link
                  key={c.href}
                  to={c.href}
                  className="block py-1.5 text-sm text-gray-700 hover:text-[#1e3a8a]"
                  onClick={() => setIsMenuOpen(false)}
                  data-testid={`header-mobile-${c.testId}`}
                >
                  {isGr ? c.gr : c.en}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }
    if (link.type === 'dropdown') {
      return (
        <div key={link.name}>
          <button
            type="button"
            onClick={() => setMobResOpen((v) => !v)}
            className="w-full flex items-center justify-between py-2 font-medium text-gray-700 hover:text-gray-900"
            data-testid="header-mobile-resources-trigger"
          >
            <span>{link.name}</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${mobResOpen ? 'rotate-180' : ''}`} />
          </button>
          {mobResOpen && (
            <div className="pl-3 border-l-2 border-gray-100 ml-1 space-y-1">
              <Link
                to="/usefull"
                className="block py-1.5 text-sm text-gray-500"
                onClick={() => setIsMenuOpen(false)}
              >
                {i18n.language === 'gr' ? 'Όλα · Επισκόπηση' : 'All · Overview'}
              </Link>
              {RESOURCE_SECTIONS.map((s) => {
                const to = s.kind === 'static' && s.static_path ? s.static_path : `/usefull/${s.slug}`;
                return (
                  <Link
                    key={s.slug}
                    to={to}
                    className="block py-1.5 text-sm text-gray-700 hover:text-[#1e3a8a]"
                    onClick={() => setIsMenuOpen(false)}
                    data-testid={`header-mobile-resources-${s.slug}`}
                  >
                    {tSectionLabel(s, i18n.language)}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    if (link.href.startsWith('#')) {
      return (
        <a key={link.name} href={link.href} className="block text-gray-700 hover:text-gray-900 font-medium py-2" onClick={() => setIsMenuOpen(false)}>
          {link.name}
        </a>
      );
    }
    return (
      <Link
        key={link.name}
        to={link.href}
        className={`block font-medium py-2 ${isActive(link.href) ? 'text-[#1e3a8a]' : 'text-gray-700 hover:text-gray-900'}`}
        onClick={() => setIsMenuOpen(false)}
      >
        {link.name}
      </Link>
    );
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
      <div className="bg-gray-50 border-b border-gray-100" data-testid="header-topbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/*
            Iter447 — Mobile touch-target compliance (WCAG 2.5.5). The topbar
            phone / email / social icons used to render at 14×14 px which
            failed on-phone thumb hits. Wrapped each link in a 40 px padded
            hit-box (min-h-10 min-w-10) while keeping the visual icon size,
            so the surface tap area is now comfortable.
          */}
          <div className="flex items-center justify-between h-11 sm:h-9 text-xs text-gray-600">
            <div className="flex items-center gap-1 sm:gap-5">
              <a href="tel:+302109911537" aria-label="Τηλέφωνο: 210 9911537" className="inline-flex items-center gap-1.5 min-h-10 sm:min-h-0 min-w-10 sm:min-w-0 justify-center sm:justify-start px-2 sm:px-0 hover:text-gray-900 transition-colors" data-testid="topbar-phone">
                <Phone className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">210 9911537</span>
              </a>
              <a href="mailto:info@eosh.gr" aria-label="Email: info@eosh.gr" className="inline-flex items-center gap-1.5 min-h-10 sm:min-h-0 min-w-10 sm:min-w-0 justify-center sm:justify-start px-2 sm:px-0 hover:text-gray-900 transition-colors" data-testid="topbar-email">
                <Mail className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">info@eosh.gr</span>
              </a>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1 sm:gap-3">
                <a href="https://www.facebook.com/AlpineClubIlioupolis" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="inline-flex items-center justify-center min-h-10 min-w-10 sm:min-h-0 sm:min-w-0 hover:text-[#1e3a8a] transition-colors" data-testid="topbar-facebook">
                  <Facebook className="w-3.5 h-3.5" />
                </a>
                <a href="https://www.instagram.com/eos_ilioupolis/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="inline-flex items-center justify-center min-h-10 min-w-10 sm:min-h-0 sm:min-w-0 hover:text-[#1e3a8a] transition-colors" data-testid="topbar-instagram">
                  <Instagram className="w-3.5 h-3.5" />
                </a>
                <a href="https://www.youtube.com/channel/UCQgsNgg0qyFbPRM6uqsbXpg" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="inline-flex items-center justify-center min-h-10 min-w-10 sm:min-h-0 sm:min-w-0 hover:text-[#1e3a8a] transition-colors" data-testid="topbar-youtube">
                  <Youtube className="w-4 h-4" />
                </a>
              </div>
              <div className="hidden md:flex items-center gap-4">
                <span className="w-px h-4 bg-gray-300" aria-hidden="true" />
                <button
                  onClick={toggleLanguage}
                  className="flex items-center gap-1.5 hover:text-gray-900 font-medium transition-colors"
                  aria-label="Change language"
                  data-lang-toggle
                  data-testid="header-lang-toggle"
                >
                  <Globe className="w-3.5 h-3.5" />
                  <span>{i18n.language === 'gr' ? 'ΕΛ' : 'EN'}</span>
                </button>
                <span className="w-px h-4 bg-gray-300" aria-hidden="true" />
                {user ? (
                  <Link
                    to={hasAnyAdminScope(user) ? (ROLE_HOME[user.role] || '/admin') : '/portal'}
                    className="flex items-center gap-1.5 font-semibold text-[#1e3a8a] hover:text-[#1e40af] transition-colors"
                    data-testid="header-portal-btn"
                  >
                    <User className="w-3.5 h-3.5" />
                    {hasAnyAdminScope(user)
                      ? (i18n.language === 'gr' ? (STAFF_CTA_LABEL[user.role]?.gr || 'Διαχείριση') : (STAFF_CTA_LABEL[user.role]?.en || 'Admin'))
                      : (i18n.language === 'gr' ? 'Το προφίλ μου' : 'My Portal')}
                  </Link>
                ) : (
                  <>
                    <Link to="/login" className="font-medium hover:text-gray-900 transition-colors" data-testid="header-login-btn">
                      {i18n.language === 'gr' ? 'Σύνδεση' : 'Sign In'}
                    </Link>
                    <Link to="/register" className="flex items-center gap-1.5 font-semibold text-[#1e3a8a] hover:text-[#1e40af] transition-colors" data-testid="header-join-btn">
                      <User className="w-3.5 h-3.5" />
                      {t('nav.joinNow')}
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <img src="/logo.png" alt="" width="56" height="56" fetchPriority="high" className="h-12 w-12 sm:h-14 sm:w-14" />
            <div className="flex flex-col leading-tight">
              <span className="text-base sm:text-lg font-bold bg-gradient-to-r from-[#4b6fc9] via-[#3a5fbf] to-[#1e3a8a] bg-clip-text text-transparent">
                ΕΟΣ ΗΛΙΟΥΠΟΛΗΣ
              </span>
              <span className="text-xs sm:text-sm text-gray-600 font-medium hidden sm:block">Ilioupolis Alpine Club</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map(renderDesktopLink)}
            <button
              onClick={() => navigate('/search')}
              className="text-gray-700 hover:text-gray-900 transition-colors"
              aria-label={i18n.language === 'gr' ? 'Αναζήτηση' : 'Search'}
              data-testid="header-search-btn"
            >
              <Search className="w-5 h-5" />
            </button>
          </nav>

          <button
            className="md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
            data-testid="header-burger-btn"
          >
            {isMenuOpen ? <X className="w-6 h-6 text-gray-900" /> : <Menu className="w-6 h-6 text-gray-900" />}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <nav className="px-4 py-4 space-y-3">
            {navLinks.map(renderMobileLink)}
            <Link
              to="/search"
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium py-2"
              data-testid="header-mobile-search"
            >
              <Search className="w-5 h-5" />
              <span>{i18n.language === 'gr' ? 'Αναζήτηση' : 'Search'}</span>
            </Link>
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium py-2 w-full"
              data-lang-toggle
              data-testid="header-mobile-lang-toggle"
            >
              <Globe className="w-5 h-5" />
              <span>{i18n.language === 'en' ? 'Ελληνικά' : 'English'}</span>
            </button>
            {user ? (
              <Button asChild className="w-full bg-[#1e3a8a] hover:bg-[#1e40af] text-white mt-4">
                <Link to={hasAnyAdminScope(user) ? (ROLE_HOME[user.role] || '/admin') : '/portal'} onClick={() => setIsMenuOpen(false)}>
                  {hasAnyAdminScope(user)
                    ? (i18n.language === 'gr' ? (STAFF_CTA_LABEL[user.role]?.gr || 'Διαχείριση') : (STAFF_CTA_LABEL[user.role]?.en || 'Admin'))
                    : (i18n.language === 'gr' ? 'Το προφίλ μου' : 'My Portal')}
                </Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="outline" className="w-full mt-4">
                  <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                    {i18n.language === 'gr' ? 'Σύνδεση' : 'Sign In'}
                  </Link>
                </Button>
                <Button asChild className="w-full bg-[#1e3a8a] hover:bg-[#1e40af] text-white mt-2">
                  <Link to="/register" onClick={() => setIsMenuOpen(false)}>{t('nav.joinNow')}</Link>
                </Button>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
